import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, ArrowRight } from 'lucide-react';
import axios from 'axios';
import API_URL from '../components/config'; 

const Hero = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { slug } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    courseInterested: '',
    priorExperience: '',
    goals: ''
  });

  useEffect(() => {
    if (!slug) {
      setError('No program specified.');
      setLoading(false);
      return;
    }
    axios.get(`${API_URL}/api/programs/${slug}`)
      .then(res => {
        const programName = res.data.name || res.data.title || '';
        if (!programName) {
          setError('Program name not found.');
        } else {
          setFormData(prev => ({
            ...prev,
            courseInterested: programName
          }));
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to fetch program details.');
        setLoading(false);
      });
  }, [slug]);

  const validate = () => {
    if (!formData.firstName.trim()) {
      setSubmitError("First name is required.");
      return false;
    }
    if (!formData.lastName.trim()) {
      setSubmitError("Last name is required.");
      return false;
    }
    if (!formData.email.trim()) {
      setSubmitError("Email address is required.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitError("Please enter a valid email address.");
      return false;
    }
    if (!formData.courseInterested.trim()) {
      setSubmitError("Program is required.");
      return false;
    }
    if (!formData.goals.trim()) {
      setSubmitError("Please share your learning goals.");
      return false;
    }
    return true;
  };  
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/apply`, formData);
      navigate('/thank-you');
    } catch (err) {
      setSubmitError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to submit application. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex justify-center items-center text-white">
        Loading ...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex justify-center items-center text-red-500 px-6 text-center">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-light">
      {/* Hero Section */}
      <div className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 30px 30px, white 2px, transparent 2px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-8">
            Application • Premium Access • Limited Cohort
          </p>
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-white to-transparent mx-auto mb-8"></div>
          <p className="text-xl md:text-2xl font-light leading-relaxed max-w-3xl mx-auto tracking-wide">
            Reserve your position among industry leaders transforming business through Artificial Intelligence.
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="bg-white text-black">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="mb-16">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">Application Form</p>
            <h2 className="text-4xl font-thin mb-6">
              Begin Your
              <br />
              <em className="text-gray-400">Transformation</em>
            </h2>
            <div className="w-20 h-px bg-gray-300 mb-8"></div>
          </div>

          {/* Status Messages */}
          {submitting && (
            <div className="text-center text-gray-600 bg-gray-100 rounded-none py-4 mb-8 border-l-4 border-gray-400">
              <p className="font-light tracking-wide">Processing your application...</p>
            </div>
          )}
          {submitError && (
            <div className="text-center text-red-700 bg-red-50 rounded-none py-4 mb-8 border-l-4 border-red-400">
              <p className="font-light">{submitError}</p>
            </div>
          )}
          {submitSuccess && (
            <div className="text-center text-green-700 bg-green-50 rounded-none py-4 mb-8 border-l-4 border-green-400">
              <p className="font-light">{submitSuccess}</p>
            </div>
          )}

          <div className="space-y-12">
            {/* Personal Information Section */}
            <div className=" pb-8">
              <h3 className="text-2xl font-thin mb-8 tracking-wide">Personal Details</h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="group">
                  <label htmlFor="firstName" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your first name"
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light"
                  />
                </div>
                
                <div className="group">
                  <label htmlFor="lastName" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your last name"
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="pb-8">
              <h3 className="text-2xl font-thin mb-8 tracking-wide">Contact Information</h3>
              
              <div className="space-y-8">
                <div className="group">
                  <label htmlFor="email" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@company.com"
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light"
                  />
                </div>

                <div className="group">
                  <label htmlFor="phone" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+234 (123) 456-7890"
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light"
                  />
                </div>

                <div className="group">
                  <label htmlFor="organization" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    Organization / Company
                  </label>
                  <input
                    id="organization"
                    name="organization"
                    type="text"
                    value={formData.organization}
                    onChange={handleInputChange}
                    placeholder="Your organization name"
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light"
                  />
                </div>
              </div>
            </div>

            {/* Program Information Section */}
            <div className="pb-8">
              <h3 className="text-2xl font-thin mb-8 tracking-wide">Program</h3>
              
              <div className="group">
                <label htmlFor="courseInterested" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                  Program <span className="text-red-500">*</span>
                </label>
                <input
                    id="courseInterested"
                    name="courseInterested"
                    type="text"
                    value={formData.courseInterested}
                    readOnly
                    required
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light"
                  />
              </div>
            </div>

            {/* Background & Goals Section */}
            <div className="space-y-8">
              <h3 className="text-2xl font-thin mb-8 tracking-wide">Background & Aspirations</h3>
              
              <div className="group">
                <label htmlFor="priorExperience" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                  Prior AI or Technology Experience
                </label>
                <textarea
                  id="priorExperience"
                  name="priorExperience"
                  value={formData.priorExperience}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Describe your background in AI, technology, or related fields..."
                  className="w-full bg-transparent border border-gray-300 p-4 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light resize-none"
                />
              </div>

              <div className="group">
                <label htmlFor="goals" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                  Learning Objectives & Goals <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="goals"
                  name="goals"
                  value={formData.goals}
                  onChange={handleInputChange}
                  rows={5}
                  required
                  placeholder="What do you hope to achieve through this program? How will it advance your career or organization?"
                  className="w-full bg-transparent border border-gray-300 p-4 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light resize-none"
                />
              </div>
            </div>

            {/* Submit Section */}
            <div className="pt-12 text-center">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="group bg-black text-white px-16 py-4 text-sm tracking-[0.2em] uppercase hover:bg-gray-800 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="flex items-center justify-center gap-3">
                  <Send size={18} />
                  {submitting ? "Submitting Application..." : "Submit Application"}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Quote */}
      <div className="bg-gray-100 py-10 px-6 text-black">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-6xl font-thin text-gray-800 mb-6">"</div>
          <blockquote className="text-2xl md:text-3xl font-light leading-relaxed tracking-wide">
            The future belongs to those who understand that AI is not about replacing human intelligence,
            <em className="text-gray-400"> it's about amplifying it.</em>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Hero;